import { Descriptions } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { deleteEducationArticle, getEducationArticle } from '../../../api/homepageManagementEducationInfo.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import { IGetEducationArticle } from '../../../models/homepageManagementEducationInfo.model';

interface IMatch {
  bulletinCode: string;
}

const HomepageEducationRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const bulletinCode = Number(match.params.bulletinCode);
  const history = useHistory();
  const location = useLocation();
  const { successNotification } = useNotification(); 
  const { data: res, isLoading, error, execute } = useAxios<IGetEducationArticle>();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios<IGetEducationArticle>();

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (bulletinCode) {
      execute(getEducationArticle(bulletinCode));
    }
  }, [bulletinCode]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      successNotification(t('common.message.delete'));
      history.push(`/homepage/education${location.search}`);
    }
  }, [deleteRes, deleteIsLoading]);

  const onDelete = () => {
    deleteExecute(deleteEducationArticle(bulletinCode));
  };

  return (
    <div className="content scrollbar">
      {(isLoading || deleteIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.education.title3')}
      />
      <div className='comm-table__area rowType mt-0'>
        <Descriptions
          bordered
          column={4}
          size="small"
          labelStyle={{ width: '12.5%' }}
          contentStyle={{ width: '12.5%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={4}>
            {res?.data?.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column2')}>
            {getCommonCodeLabel('BR0014', res?.data?.bulletinDiv || '', 'ko')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column3')}>
            {getCommonCodeLabel(
              res?.data?.bulletinDiv === 'LE' ? 'BR0015' : 'BR0016',
              res?.data?.bulletinBrn || '',
              'ko',
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column8')}>
            {moment(res?.data?.regDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column7')}>
            {res?.data?.readCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column4')} span={4}>
            {res?.data?.url || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.contents')} span={4}>
            <div
              dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column6')} span={4}>
            {res?.data?.thumbnail ? (
              <a
                className="download-link mr-1"
                href={res?.data?.thumbnail.fileLocation}
                download={res?.data?.thumbnail.originalName}
              >
                {res?.data?.thumbnail.originalName}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.education.column7')} span={4}>
            <img
              src={res?.data?.thumbnail?.fileLocation}
              alt=""
              style={{ maxWidth: '400px', maxHeight: '400px' }}
            />
          </Descriptions.Item>
        </Descriptions>

        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              text={t('common.button.list')}
              onClick={() => {
                history.push(`/homepage/education${location.search}`);
              }}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic gray"
              text={t('common.button.delete')}
              onClick={() => setConfirmDeleteVisible(true)}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn-basic pink-line"
              text={t('common.button.edit')}
              onClick={() =>
                history.push(
                  `/homepage/education/${bulletinCode}/edit${location.search}`,
                )
              }
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible &&
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('homepage.education.1')}
          messageText={t('homepage.education.2')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      }
    </div>
  );
};

export default HomepageEducationRead;
